/**
 * Västtrafik's buses and trams, from their Planera Resa v4 API, as trips
 * (`trip.ts`): each with its line's own colours, its stops and their best
 * known times, and the way it drives.
 *
 * Two halves. `gatherNearby` runs in the game's Worker (`realtime/worker.ts`),
 * which holds the OAuth client's key and secret: a secret in a page is a
 * published one. Around a point it finds the stop areas, their departures
 * from a quarter of an hour ago to twenty-five minutes on, and each bus
 * journey's details: its calls on the way, with times, and its coordinates.
 * That is every bus that is near now or soon will be, and where each one
 * is, without a position feed. The answer is compact (`CompactTrips`), and
 * `readTrips` makes it trips in the page.
 *
 * Which of Västtrafik's three designs a bus wears goes by its line: express
 * for the coloured express lines, metro for Gothenburg's trunk lines
 * (stombussar), and the area bus for all the rest. See `lookOf`.
 *
 * Pure: the Worker passes in its fetch.
 */

import type { BusLook, TransitTrip } from './trip.js';

/** What the Worker sends: trips as plain arrays, times in ms. */
export interface CompactTrips {
  /** When it was gathered, ms. */
  at: number;
  trips: Array<{
    id: string;
    line: { name: string; bg: string; fg: string; border: string; mode: string };
    headsign: string;
    /** Each stop: lat, lon, name, arrival, departure. */
    stops: Array<[number, number, string, number, number]>;
    /** lat, lon pairs, rounded to a few centimetres. */
    path: number[];
  }>;
}

/** Gothenburg's trunk bus lines: Västtrafik's metro design. */
const METRO_LINES = new Set(['16', '17', '18', '19', '25', '50', '55', '58', '60']);

/** Which design a line's buses wear. */
export function lookOf(name: string): BusLook {
  const n = name.trim();
  // The airport coaches are Flygbussarna's, in their own colours.
  if (/^flyg/i.test(n)) return 'plain';
  if (/express/i.test(n) || /^x\d+/i.test(n) || (n !== '' && !/\d/.test(n))) return 'express';
  if (METRO_LINES.has(n)) return 'metro';
  return 'area';
}

/** The trips in a Worker's answer. Malformed ones are left out. */
export function readTrips(data: unknown): TransitTrip[] {
  const trips = (data as Partial<CompactTrips> | null)?.trips;
  if (!Array.isArray(trips)) return [];
  const out: TransitTrip[] = [];
  for (const t of trips) {
    if (typeof t?.id !== 'string' || !Array.isArray(t.stops) || !Array.isArray(t.path) || t.stops.length < 2 || t.path.length < 4) continue;
    const name = String(t.line?.name ?? '');
    out.push({
      id: `vt:${t.id}`,
      line: {
        name,
        mode: t.line?.mode === 'bus' ? 'bus' : t.line?.mode === 'tram' ? 'tram' : 'other',
        look: lookOf(name),
        background: colour(t.line?.bg, '#009ddc'),
        foreground: colour(t.line?.fg, '#ffffff'),
        border: colour(t.line?.border, '#009ddc'),
      },
      headsign: signOf(String(t.headsign ?? '')),
      stops: t.stops.map(([lat, lon, stopName, arrive, depart]) => ({ lat, lon, name: stopName, arrive, depart })),
      path: t.path,
    });
  }
  return out;
}

/**
 * What the sign says: the direction without the boarding notes Västtrafik
 * puts after it ("Fredriksdal, Påstigning fram"), which are not on the bus.
 */
export function signOf(direction: string): string {
  return direction.replace(/,\s*påstigning[^,]*/gi, '').trim();
}

function colour(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : fallback;
}

/** A GET against Planera Resa v4: its path under /pr/v4, its query; the JSON, or undefined on failure. */
export type PlaneraResa = (path: string, query: Record<string, string | string[]>) => Promise<unknown>;

export interface GatherOptions {
  /** How far round the point stop areas are looked for, metres. */
  radius: number;
  /** How many stop areas' departures are read. */
  stopAreas: number;
  /** Most journeys whose details are fetched. */
  journeys: number;
}

const DEFAULT_GATHER: GatherOptions = { radius: 1200, stopAreas: 6, journeys: 32 };

/** A list in an answer: the answer itself, or its `results`. */
function list(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) return value as Array<Record<string, unknown>>;
  const results = (value as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? results as Array<Record<string, unknown>> : [];
}

const time = (value: unknown): number => (typeof value === 'string' ? Date.parse(value) : NaN);

/**
 * The bus journeys about (lat, lon) at `now`, for the Worker: see the module's
 * comment. Each stop area's departures and each journey's details are one
 * call each; `get` may cache them.
 */
export async function gatherNearby(get: PlaneraResa, lat: number, lon: number, now: number, options: Partial<GatherOptions> = {}): Promise<CompactTrips> {
  const o = { ...DEFAULT_GATHER, ...options };
  const places = list(await get('/locations/by-coordinates', {
    latitude: lat.toFixed(5), longitude: lon.toFixed(5), radiusInMeters: String(o.radius), types: 'stoparea', limit: String(o.stopAreas),
  }));
  const areas = places.map(p => String(p.gid ?? '')).filter(g => g !== '').slice(0, o.stopAreas);
  const start = new Date(now - 15 * 60_000).toISOString();
  const boards = await Promise.all(areas.map(async gid => ({
    gid,
    departures: list(await get(`/stop-areas/${gid}/departures`, {
      startDateTime: start, timeSpanInMinutes: '40', maxDeparturesPerLineAndDirection: '3', limit: '60',
    })),
  })));
  // One per journey: the departure nearest now, so a bus about now comes first.
  const found = new Map<string, { area: string; ref: string; when: number }>();
  for (const { gid, departures } of boards) {
    for (const d of departures) {
      const journey = d.serviceJourney as { gid?: string; line?: { transportMode?: string } } | undefined;
      const ref = d.detailsReference;
      const mode = journey?.line?.transportMode;
      if (journey?.gid === undefined || typeof ref !== 'string' || (mode !== 'bus' && mode !== 'tram')) continue;
      if (d.isCancelled === true) continue;
      const when = Math.abs(time(d.estimatedOtherwisePlannedTime ?? d.plannedTime) - now);
      const had = found.get(journey.gid);
      if (had === undefined || when < had.when) found.set(journey.gid, { area: gid, ref, when: Number.isFinite(when) ? when : Infinity });
    }
  }
  const chosen = [...found.entries()].sort((a, b) => a[1].when - b[1].when).slice(0, o.journeys);
  const trips = await Promise.all(chosen.map(async ([id, { area, ref }]) => {
    const details = await get(`/stop-areas/${area}/departures/${encodeURIComponent(ref)}/details`, {
      includes: ['servicejourneycalls', 'servicejourneycoordinates'],
    }) as { serviceJourneys?: Array<Record<string, unknown>> } | undefined;
    const journey = details?.serviceJourneys?.[0];
    return journey === undefined ? undefined : compactJourney(id, journey);
  }));
  return { at: now, trips: trips.filter((t): t is CompactTrips['trips'][number] => t !== undefined) };
}

/** One journey's details as a compact trip, or undefined if it has no way or too few stops. */
export function compactJourney(id: string, journey: Record<string, unknown>): CompactTrips['trips'][number] | undefined {
  const line = (journey.line ?? {}) as Record<string, unknown>;
  const calls = Array.isArray(journey.callsOnServiceJourney) ? journey.callsOnServiceJourney as Array<Record<string, unknown>> : [];
  const coordinates = Array.isArray(journey.serviceJourneyCoordinates) ? journey.serviceJourneyCoordinates as Array<Record<string, unknown>> : [];
  const stops: Array<[number, number, string, number, number]> = [];
  for (const c of calls) {
    if (c.isCancelled === true) continue;
    const point = (c.stopPoint ?? {}) as Record<string, unknown>;
    const la = Number(c.latitude ?? point.latitude), lo = Number(c.longitude ?? point.longitude);
    const arrive = time(c.estimatedOtherwisePlannedArrivalTime ?? c.estimatedArrivalTime ?? c.plannedArrivalTime);
    const depart = time(c.estimatedOtherwisePlannedDepartureTime ?? c.estimatedDepartureTime ?? c.plannedDepartureTime);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) continue;
    // The first stop has no arrival, the last no departure.
    const a = Number.isFinite(arrive) ? arrive : depart, d = Number.isFinite(depart) ? depart : arrive;
    if (!Number.isFinite(a)) continue;
    stops.push([round(la), round(lo), String(point.name ?? ''), a, Math.max(a, d)]);
  }
  const points: number[] = [];
  for (const c of coordinates) {
    const la = Number(c.latitude), lo = Number(c.longitude);
    if (Number.isFinite(la) && Number.isFinite(lo)) points.push(la, lo);
  }
  const path = simplify(points, 1.5).map(round);
  if (stops.length < 2 || path.length < 4) return undefined;
  return {
    id,
    line: {
      name: String(line.shortName ?? line.designation ?? line.name ?? ''),
      bg: String(line.backgroundColor ?? ''), fg: String(line.foregroundColor ?? ''), border: String(line.borderColor ?? ''),
      mode: String(line.transportMode ?? ''),
    },
    headsign: String(journey.direction ?? ''),
    stops,
    path,
  };
}

const round = (v: number) => Math.round(v * 1e6) / 1e6;

/**
 * A lat, lon line simplified (Douglas–Peucker) to within `tolerance` metres:
 * the API gives a point every few metres, most of them on straight road.
 */
export function simplify(points: number[], tolerance: number): number[] {
  const n = points.length / 2;
  if (n <= 2) return points.slice();
  const lat0 = points[0] * Math.PI / 180;
  const mx = 111_320 * Math.cos(lat0), mz = 110_540;
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length > 0) {
    const [a, b] = stack.pop()!;
    const ax = points[a * 2 + 1] * mx, az = points[a * 2] * mz, bx = points[b * 2 + 1] * mx, bz = points[b * 2] * mz;
    const dx = bx - ax, dz = bz - az, len2 = dx * dx + dz * dz;
    let worst = -1, at = -1;
    for (let i = a + 1; i < b; i++) {
      const px = points[i * 2 + 1] * mx, pz = points[i * 2] * mz;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2)) : 0;
      const d = Math.hypot(ax + dx * t - px, az + dz * t - pz);
      if (d > worst) { worst = d; at = i; }
    }
    if (worst > tolerance) {
      keep[at] = 1;
      stack.push([a, at], [at, b]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i] === 1) out.push(points[i * 2], points[i * 2 + 1]);
  return out;
}
